// Loading pages ahead: what the person is likely to open next is fetched
// while the browser has nothing else to do, so it paints at once.

import { useEffect, useState } from "react";
import {
  useIsFetching,
  useIsMutating,
  useQueryClient,
  type FetchQueryOptions,
} from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import type { LazyView } from "@/platform/client";

/** Any `queryOptions(…)`: their types differ by what each query answers. */
type Prefetched = Pick<FetchQueryOptions, "queryKey">;

/** Runs `run` once the browser is idle; returns what cancels it. */
function whenIdle(run: () => void): () => void {
  if (typeof requestIdleCallback === "function") {
    const id = requestIdleCallback(run);
    return () => cancelIdleCallback(id);
  }
  const id = setTimeout(run, 100);
  return () => clearTimeout(id);
}

/**
 * Fetches `queries` while the browser is idle, once the first page has
 * settled and again after each navigation or write has settled: only those
 * missing or invalidated, so nothing is asked twice. Then loads the code of
 * each of `views` not loaded yet.
 */
export function usePrefetch(
  queries: readonly Prefetched[],
  views: readonly Pick<LazyView, "preload">[],
): void {
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const busy = useIsFetching() + useIsMutating();
  const [due, setDue] = useState(true);

  // A navigation or a write may leave one missing or invalidated.
  useEffect(() => setDue(true), [pathname]);
  useEffect(
    () =>
      qc.getMutationCache().subscribe((event) => {
        if (event.type === "updated" && event.mutation.state.status !== "pending") setDue(true);
      }),
    [qc],
  );

  useEffect(() => {
    if (!due || busy > 0) return;
    return whenIdle(() => {
      // What has just begun is not counted yet where this renders.
      if (qc.isFetching() > 0 || qc.isMutating() > 0) return;
      setDue(false);
      for (const query of queries)
        void qc.prefetchQuery({ ...(query as FetchQueryOptions), staleTime: Infinity });
      // One that fails is tried again on the next round.
      for (const view of views) view.preload().catch(() => undefined);
    });
  }, [due, busy, qc, queries, views]);
}
